import * as React from 'react';
import { forwardRef, useContext, useImperativeHandle } from 'react';
import SwapRightOutlined from '@ant-design/icons/SwapRightOutlined';
import { RangePicker as RCRangePicker } from '@rc-component/picker';
import { merge } from '@rc-component/util';
import { clsx } from 'clsx';
import ContextIsolator from '../../_util/ContextIsolator';
import { useAllowClear, useZIndex } from '../../_util/hooks';
import { getMergedStatus, getStatusClassNames } from '../../_util/statusUtils';
import { devUseWarning } from '../../_util/warning';
import { ConfigContext } from '../../config-provider';
import { useComponentConfig } from '../../config-provider/context';
import DisabledContext from '../../config-provider/DisabledContext';
import useCSSVarCls from '../../config-provider/hooks/useCSSVarCls';
import useSize from '../../config-provider/hooks/useSize';
import { FormItemInputContext } from '../../form/context';
import useVariant from '../../form/hooks/useVariants';
import { useLocale } from '../../locale';
import { useCompactItemContext } from '../../space/Compact';
import useMergedPickerSemantic from '../hooks/useMergedPickerSemantic';
import enUS from '../locale/en_US';
import useStyle from '../style';
import { getRangePlaceholder } from '../util';
import { TIME } from './constant';
import useComponents from './useComponents';
import useSuffixIcon from './useSuffixIcon';
const generateRangePicker = (generateConfig) => {
    const RangePicker = forwardRef((props, ref) => {
        const { prefixCls: customizePrefixCls, getPopupContainer: customGetPopupContainer, components, className, style, classNames, styles, placement, size: customizeSize, disabled: customDisabled, bordered = true, placeholder, status: customStatus, variant: customVariant, picker, dropdownClassName, popupClassName, popupStyle, rootClassName, suffixIcon, separator, allowClear, clearIcon, ...restProps } = props;
        const pickerType = picker === TIME ? 'timePicker' : 'datePicker';
        const { suffixIcon: contextSuffixIcon, clearIcon: contextClearIcon, allowClear: contextAllowClear, } = useComponentConfig(pickerType);
        // ====================== Warning =======================
        if (process.env.NODE_ENV !== 'production') {
            const warning = devUseWarning('DatePicker.RangePicker');
            const deprecatedProps = {
                dropdownClassName: 'classNames.popup.root',
                popupClassName: 'classNames.popup.root',
                popupStyle: 'styles.popup.root',
                bordered: 'variant',
                onSelect: 'onCalendarChange',
            };
            Object.entries(deprecatedProps).forEach(([oldProp, newProp]) => {
                warning.deprecated(!(oldProp in props), oldProp, newProp);
            });
        }
        const innerRef = React.useRef(null);
        const { getPrefixCls, direction, getPopupContainer, rangePicker } = useContext(ConfigContext);
        const prefixCls = getPrefixCls('picker', customizePrefixCls);
        const { compactSize, compactItemClassnames } = useCompactItemContext(prefixCls, direction);
        const rootPrefixCls = getPrefixCls();
        const mergedSeparator = separator ?? rangePicker?.separator;
        const [variant, enableVariantCls] = useVariant('rangePicker', customVariant, bordered);
        const rootCls = useCSSVarCls(prefixCls);
        const [hashId, cssVarCls] = useStyle(prefixCls, rootCls);
        const mergedRootClassName = clsx(hashId, cssVarCls, rootCls, rootClassName);
        // ===================== Icon =====================
        const mergedAllowClear = useAllowClear({
            componentName: 'RangePicker',
            allowClear,
            clearIcon,
            contextAllowClear,
            contextClearIcon,
            defaultAllowClear: true,
        });
        // ================== components ==================
        const mergedComponents = useComponents(components);
        // ===================== Size =====================
        const mergedSize = useSize((ctx) => customizeSize ?? compactSize ?? ctx);
        // ===================== Disabled =====================
        const disabled = React.useContext(DisabledContext);
        const mergedDisabled = customDisabled ?? disabled;
        // =========== Merged Props for Semantic ===========
        const mergedProps = {
            ...props,
            size: mergedSize,
            disabled: mergedDisabled,
            status: customStatus,
            variant: customVariant,
        };
        const [mergedClassNames, mergedStyles] = useMergedPickerSemantic(pickerType, classNames, styles, popupClassName || dropdownClassName, popupStyle, mergedProps);
        // ===================== FormItemInput =====================
        const formItemContext = useContext(FormItemInputContext);
        const { hasFeedback, status: contextStatus, feedbackIcon } = formItemContext;
        const mergedSuffixIcon = useSuffixIcon({
            picker,
            hasFeedback,
            feedbackIcon,
            suffixIcon: suffixIcon === undefined ? contextSuffixIcon : suffixIcon,
        });
        useImperativeHandle(ref, () => innerRef.current);
        const [contextLocale] = useLocale('Calendar', enUS);
        const locale = merge(contextLocale, props.locale || {});
        // ============================ zIndex ============================
        const [zIndex] = useZIndex('DatePicker', mergedStyles?.popup?.root?.zIndex);
        return (React.createElement(ContextIsolator, { space: true },
            React.createElement(RCRangePicker, { separator: React.createElement("span", { "aria-label": "to", className: `${prefixCls}-separator` }, mergedSeparator ?? React.createElement(SwapRightOutlined, null)), disabled: mergedDisabled, ref: innerRef, placement: placement, placeholder: getRangePlaceholder(locale, picker, placeholder), suffixIcon: mergedSuffixIcon, prevIcon: React.createElement("span", { className: `${prefixCls}-prev-icon` }), nextIcon: React.createElement("span", { className: `${prefixCls}-next-icon` }), superPrevIcon: React.createElement("span", { className: `${prefixCls}-super-prev-icon` }), superNextIcon: React.createElement("span", { className: `${prefixCls}-super-next-icon` }), transitionName: `${rootPrefixCls}-slide-up`, picker: picker, ...restProps, locale: locale.lang, getPopupContainer: customGetPopupContainer || getPopupContainer, generateConfig: generateConfig, components: mergedComponents, direction: direction, 
                // Style
                prefixCls: prefixCls, rootClassName: mergedRootClassName, className: clsx({
                    [`${prefixCls}-large`]: mergedSize === 'large',
                    [`${prefixCls}-small`]: mergedSize === 'small',
                    [`${prefixCls}-${variant}`]: enableVariantCls,
                }, getStatusClassNames(prefixCls, getMergedStatus(contextStatus, customStatus), hasFeedback), compactItemClassnames, className, rangePicker?.className), style: { ...rangePicker?.style, ...style }, 
                // Semantic Style
                classNames: mergedClassNames, styles: {
                    ...mergedStyles,
                    popup: {
                        ...mergedStyles.popup,
                        root: {
                            ...mergedStyles.popup.root,
                            zIndex,
                        },
                    },
                }, allowClear: mergedAllowClear })));
    });
    if (process.env.NODE_ENV !== 'production') {
        RangePicker.displayName = 'RangePicker';
    }
    return RangePicker;
};
export default generateRangePicker;
